import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Mail, Building2 } from "lucide-react";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    name: "", phone: "", address_line1: "", city: "", state: "", zip_code: "", date_of_birth: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      const p = user.profile || {};
      setForm({
        name: user.name || "", phone: p.phone || "", address_line1: p.address_line1 || "",
        city: p.city || "", state: p.state || "", zip_code: p.zip_code || "",
        date_of_birth: (p.date_of_birth || "").slice(0, 10),
      });
      api.get("/consumer/household-profile").then((r) => {
        const d = r.data?.details || {};
        setForm((f) => ({ ...f, phone: f.phone || d.phone || "", zip_code: f.zip_code || d.zip_code || "",
          state: f.state || d.state || "", city: f.city || d.city || "", address_line1: f.address_line1 || d.address_line1 || "" }));
      }).catch(() => {});
    }
  }, [user]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/profile", form);
      await refresh();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 sm:p-10 lg:p-12 max-w-3xl mx-auto animate-rise">
      <PageHeader eyebrow="Account" title="Profile & settings" subtitle="Keep your details current so eligibility and coverage stay accurate." />

      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <InfoTile icon={Mail} label="Email" value={user?.email} />
        {user?.employer_id && (
          <>
            <InfoTile icon={ShieldCheck} label="Role" value={(user?.role || "").replace("_", " ")} capitalize />
            <InfoTile icon={Building2} label="Employer" value="Linked" />
          </>
        )}
      </div>

      <form onSubmit={save} className="bg-white rounded-xl border border-mist-200 p-8 space-y-5">
        <div>
          <Label className="font-semibold">Full name</Label>
          <Input value={form.name} onChange={upd("name")} className="mt-1.5" data-testid="profile-name" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="font-semibold">Phone</Label>
            <Input value={form.phone} onChange={upd("phone")} className="mt-1.5" data-testid="profile-phone" />
          </div>
          <div>
            <Label className="font-semibold">Date of birth</Label>
            <Input type="date" value={form.date_of_birth} onChange={upd("date_of_birth")} className="mt-1.5" data-testid="profile-dob" />
          </div>
        </div>
        <div>
          <Label className="font-semibold">Address</Label>
          <Input value={form.address_line1} onChange={upd("address_line1")} className="mt-1.5" placeholder="Street address" data-testid="profile-address" />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label className="font-semibold">City</Label>
            <Input value={form.city} onChange={upd("city")} className="mt-1.5" data-testid="profile-city" />
          </div>
          <div>
            <Label className="font-semibold">State</Label>
            <Input value={form.state} onChange={upd("state")} className="mt-1.5" maxLength={2} data-testid="profile-state" />
          </div>
          <div>
            <Label className="font-semibold">ZIP</Label>
            <Input value={form.zip_code} onChange={upd("zip_code")} className="mt-1.5" data-testid="profile-zip" />
          </div>
        </div>
        <div className="flex justify-end pt-2">
          <Button type="submit" disabled={saving} className="bg-gold text-navy-900 hover:bg-gold-light rounded-full font-bold" data-testid="save-profile">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function InfoTile({ icon: Icon, label, value, capitalize }) {
  return (
    <div className="bg-white rounded-xl border border-mist-200 p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg bg-mist-100 flex items-center justify-center"><Icon className="w-5 h-5 text-navy-800" /></div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className={`font-semibold text-navy-900 text-sm truncate ${capitalize ? "capitalize" : ""}`}>{value}</p>
      </div>
    </div>
  );
}
